import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, HandCoins } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContributionGatheringFields } from '@/components/giving/contribution-gathering-fields';
import {
  ContributionGiverFields,
  contributionGiverPayload,
  contributionGiverReady,
  type ContributionGiverKind,
} from '@/components/giving/contribution-giver-fields';
import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { FundDetailDialog } from '@/components/giving/fund-detail-dialog';
import { VoidReasonDialog } from '@/components/giving/void-reason-dialog';
import { RecordContributionSaveOptions } from '@/components/giving/record-contribution-save-options';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import {
  DateRangeFilter,
  INSIGHTS_DATE_RANGE_PRESETS,
  LIST_FILTER_DATE_RANGE_PRESETS,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FilterBar } from '@/components/shared/filter-bar';
import { FundSelect } from '@/components/shared/fund-select';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { GivingTrendChart } from '@/components/giving/giving-trend-chart';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useGivingSettings } from '@/hooks/use-giving-settings';
import { api } from '@/lib/api';
import {
  dataTableMoneyColumnTotals,
  formatInsightsMoneySummary,
  insightsHasMultipleCurrencies,
} from '@/lib/money-totals';
import { ApiError } from '@/lib/api-errors';
import { resolveOfferingOccurrence } from '@/lib/resolve-offering-occurrence';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import { resolveDefaultFund } from '@/lib/giving-default-funds';
import {
  loadRecordContributionPreferences,
  persistRecordContributionPreferences,
} from '@/lib/record-contribution-preferences';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { ContributionAccountField } from '@/components/giving/contribution-account-field';
import {
  accountKindForMethod,
  CONTRIBUTION_METHODS,
  type ContributionMethod,
} from '@/lib/contribution-methods';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

interface GivingInsights {
  amountMinor: number;
  count: number;
  currency: string;
  byCurrency?: Array<{ currency: string; amountMinor: number; count: number }>;
  comparison: {
    change: number;
    previousAmountMinor: number;
    previousCount: number;
  };
  byFund: Array<{ fundId: string; fundName: string; amountMinor: number; currency: string }>;
  trend: Array<{
    key: string;
    label: string;
    amountMinor: number;
    count: number;
    grain?: 'day' | 'week' | 'month';
  }>;
}

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

interface Contribution {
  id: string;
  fundId: string;
  memberId: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  canVoid: boolean;
}

const METHOD_ANY = 'any';
const FUND_ANY = 'any';
const VOIDED_INCLUDE = 'include';
const VOIDED_HIDE = 'hide';

export const ContributionsPage = () => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';
  const [insightsDateRange, setInsightsDateRange] = useState<DateRange>(() => ({
    preset: 'thisMonth',
    ...resolvePreset('thisMonth', timezone),
  }));
  const [filterDateRange, setFilterDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));
  const [search, setSearch] = useState('');
  const [filterMemberId, setFilterMemberId] = useState<string | null>(null);
  const [filterMemberQuery, setFilterMemberQuery] = useState('');
  const [filterMemberSelected, setFilterMemberSelected] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [filterFundId, setFilterFundId] = useState(FUND_ANY);
  const [filterMethod, setFilterMethod] = useState(METHOD_ANY);
  const [voidedFilter, setVoidedFilter] = useState(VOIDED_HIDE);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [open, setOpen] = useState(false);
  const [detailContributionId, setDetailContributionId] = useState<string | null>(null);
  const [detailFundId, setDetailFundId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<Contribution | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPassword, setVoidPassword] = useState('');
  const [reasonTarget, setReasonTarget] = useState<Contribution | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [fundCurrency, setFundCurrency] = useState<string | null>(null);
  const [giverKind, setGiverKind] = useState<ContributionGiverKind>('internal');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [externalPartyId, setExternalPartyId] = useState<string | null>(null);
  const [giverName, setGiverName] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [contributionDate, setContributionDate] = useState<string | null>(null);
  const [programId, setProgramId] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState<string | null>(null);
  const [method, setMethod] = useState<ContributionMethod>('cash');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [savePreferences, setSavePreferences] = useState(() =>
    loadRecordContributionPreferences(church?.id),
  );
  const [formKey, setFormKey] = useState(0);

  const givingSettings = useGivingSettings();

  const canRecord = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canVoidContribution = hasPermission(membership, PERMISSIONS.GIVING_VOID);

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () =>
      api.get<{ data: Array<{ id: string; name: string; defaultCurrency: string; isActive: boolean }> }>(
        '/giving/funds?pageSize=100&activeOnly=true',
      ),
  });

  const applyDefaultFund = () => {
    const defaults = resolveDefaultFund(givingSettings.data, 'contribution', funds.data?.data);
    setFundId(defaults.fundId);
    setFundCurrency(defaults.currency);
  };

  const resetRecordForm = () => {
    setGiverKind('internal');
    setMemberId(null);
    setExternalPartyId(null);
    setGiverName('');
    setAmountMinor(null);
    setContributionDate(null);
    setProgramId(null);
    setServiceDate(null);
    setMethod('cash');
    setAccountId(null);
    setReference('');
    setNote('');
    applyDefaultFund();
  };

  useEffect(() => {
    if (!open) return;
    applyDefaultFund();
  }, [open]);

  useEffect(() => {
    if (!open || fundId != null) return;
    if (!funds.data?.data?.length || !givingSettings.data) return;
    applyDefaultFund();
  }, [open, fundId, funds.data?.data, givingSettings.data]);

  const filterMembers = useQuery({
    queryKey: ['people', 'members', 'picker', 'contribution-filter', filterMemberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>(
        `/people/members?pageSize=20${
          filterMemberQuery ? `&q=${encodeURIComponent(filterMemberQuery)}` : ''
        }`,
      ),
    placeholderData: keepPreviousData,
  });

  const list = useQuery({
    queryKey: [
      'giving',
      'contributions',
      page,
      pageSize,
      search,
      filterMemberId,
      filterFundId,
      filterMethod,
      voidedFilter,
      filterDateRange.from,
      filterDateRange.to,
    ],
    queryFn: () =>
      api.get<{
        data: Contribution[];
        page: {
          page: number;
          pageSize: number;
          totalItems: number;
          totalPages: number;
        };
        totals?: { amountMinor: number; count: number };
      }>('/giving/contributions', {
        query: {
          page,
          pageSize,
          q: search.trim() || undefined,
          memberId: filterMemberId ?? undefined,
          fundId: filterFundId === FUND_ANY ? undefined : filterFundId,
          method: filterMethod === METHOD_ANY ? undefined : filterMethod,
          dateFrom: filterDateRange.from ?? undefined,
          dateTo: filterDateRange.to ?? undefined,
          includeVoided: voidedFilter === VOIDED_INCLUDE ? true : undefined,
        },
      }),
    placeholderData: keepPreviousData,
  });

  const insights = useQuery({
    queryKey: ['giving', 'insights', insightsDateRange.from, insightsDateRange.to],
    queryFn: () =>
      api.get<GivingInsights>('/giving/insights', {
        query: {
          dateFrom: insightsDateRange.from ?? undefined,
          dateTo: insightsDateRange.to ?? undefined,
        },
      }),
  });

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (filterMemberId) count += 1;
    if (filterFundId !== FUND_ANY) count += 1;
    if (filterMethod !== METHOD_ANY) count += 1;
    if (filterDateRange.preset !== 'allTime') count += 1;
    if (voidedFilter === VOIDED_INCLUDE) count += 1;
    return count;
  }, [
    filterDateRange.preset,
    filterFundId,
    filterMemberId,
    filterMethod,
    search,
    voidedFilter,
  ]);

  const currency = fundCurrency ?? church?.baseCurrency ?? 'GHS';

  const giver = { kind: giverKind, memberId, externalPartyId, giverName };
  const giverReady = contributionGiverReady(giver);

  const filterMemberOptions = useMemo(
    () =>
      (filterMembers.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [filterMembers.data?.data],
  );
  const selectedFilterMember =
    filterMemberSelected ??
    filterMemberOptions.find((option) => option.id === filterMemberId) ??
    null;

  const record = useMutation({
    mutationFn: async () => {
      const occurrenceId =
        programId && serviceDate ? await resolveOfferingOccurrence(programId, serviceDate) : null;
      return api.post<Contribution>('/giving/contributions', {
        fundId,
        ...contributionGiverPayload(giver),
        amountMinor,
        currency,
        contributionDate,
        occurrenceId,
        method,
        financialPositionItemId: accountKindForMethod(method) ? accountId : null,
        reference: reference || null,
        note: note || null,
      });
    },
    onSuccess: () => {
      toast.success(t('saved'));
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'summary'] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      if (savePreferences.addAnother) {
        resetRecordForm();
        setFormKey((current) => current + 1);
        return;
      }
      setOpen(false);
      setFundId(null);
      setFundCurrency(null);
      setGiverKind('internal');
      setMemberId(null);
      setExternalPartyId(null);
      setGiverName('');
      setAmountMinor(null);
      setContributionDate(null);
      setProgramId(null);
      setServiceDate(null);
      setMethod('cash');
    setAccountId(null);
      setReference('');
      setNote('');
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const voidContribution = useMutation({
    mutationFn: () =>
      api.post<Contribution>(`/giving/contributions/${voidTarget!.id}/void`, {
        reason: voidReason,
        password: voidPassword,
      }),
    onSuccess: () => {
      toast.success(t('voided'));
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      void queryClient.invalidateQueries({ queryKey: ['giving', 'contributions'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const fundName = (id: string) => funds.data?.data.find((fund) => fund.id === id)?.name ?? id;

  const columns: DataTableColumn<Contribution>[] = [
    {
      id: 'date',
      header: t('contributions.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'amount',
      header: t('contributions.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'fund',
      header: t('contributions.columns.fund'),
      cell: (row) => (
        <button
          type="button"
          className="rounded-sm text-left text-body-sm text-fg underline-offset-2 hover:text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          onClick={(event) => {
            event.stopPropagation();
            setDetailFundId(row.fundId);
          }}
        >
          {fundName(row.fundId)}
        </button>
      ),
    },
    {
      id: 'giver',
      header: t('contributions.columns.giver'),
      cell: (row) => {
        if (row.identityHidden) return t('contributions.identityHidden');
        if (!row.giverName) return '—';
        if (row.memberId) {
          return <MemberNameLink memberId={row.memberId} name={row.giverName} />;
        }
        return row.giverName;
      },
    },
    {
      id: 'method',
      header: t('contributions.columns.method'),
      cell: (row) => t(`methods.${row.method}` as 'methods.cash'),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{t('contributions.voided')}</Badge>
        ) : row.identityHidden ? (
          <Badge variant="neutral">{t('contributions.identityHidden')}</Badge>
        ) : (
          '—'
        ),
    },
  ];

  const rows = list.data?.data ?? [];
  const totalsCurrency = church?.baseCurrency ?? 'GHS';

  return (
    <div>
      <PageHeader
        title={t('contributions.title')}
        description={t('description')}
        tipId="giving.giverIdentity"
        headerActions={
          canRecord ? (
            <Button onClick={() => setOpen(true)}>{t('contributions.record')}</Button>
          ) : undefined
        }
      />
      <GivingSubnav />

      <CollapsibleInsights
        summary={[
          {
            label: t('contributions.insights.total'),
            value: formatInsightsMoneySummary(insights.data, church?.baseCurrency ?? 'GHS'),
          },
          {
            label: t('contributions.insights.gifts'),
            value: insights.data ? formatNumber(insights.data.count) : '…',
          },
        ]}
      >
        <DateRangeFilter
          value={insightsDateRange}
          onChange={setInsightsDateRange}
          labels={{
            today: tCommon('date.today'),
            yesterday: tCommon('date.yesterday'),
            thisWeek: tCommon('date.thisWeek'),
            lastWeek: tCommon('date.lastWeek'),
            thisMonth: tCommon('date.thisMonth'),
            lastMonth: tCommon('date.lastMonth'),
            thisQuarter: tCommon('date.thisQuarter'),
            thisYear: tCommon('date.thisYear'),
            lastYear: tCommon('date.lastYear'),
            allTime: tCommon('date.allTime'),
            custom: tCommon('date.custom'),
            from: t('form.dateFrom'),
            to: t('form.dateTo'),
          }}
          presets={INSIGHTS_DATE_RANGE_PRESETS}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          {insightsHasMultipleCurrencies(insights.data) ? (
            insights.data!.byCurrency!.map((item) => (
              <StatCard
                key={item.currency}
                label={t('contributions.insights.total')}
                money={{ amount: item.amountMinor, currency: item.currency }}
                icon={<HandCoins className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t('contributions.insights.total')}
              money={
                insights.data
                  ? { amount: insights.data.amountMinor, currency: insights.data.currency }
                  : undefined
              }
              icon={<HandCoins className="size-4" aria-hidden />}
              loading={insights.isLoading}
              comparison={
                insights.data
                  ? {
                      change: insights.data.comparison.change,
                      label: t('contributions.insights.vsPreviousPeriod'),
                      formatted: (() => {
                        const change = insights.data.comparison.change;
                        const formatted = formatPercent(Math.abs(change));
                        if (change > 0) return `+${formatted}`;
                        if (change < 0) return `-${formatted}`;
                        return formatted;
                      })(),
                    }
                  : undefined
              }
            />
          )}
          <StatCard
            label={t('contributions.insights.gifts')}
            value={insights.data ? formatNumber(insights.data.count) : undefined}
            icon={<Gift className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <GivingTrendChart
            title={t('contributions.insights.trend')}
            emptyTitle={t('contributions.insights.trendEmpty')}
            dateFrom={insightsDateRange.from}
            dateTo={insightsDateRange.to}
            baseCurrency={church?.baseCurrency ?? 'GHS'}
            fallbackPoints={(insights.data?.trend ?? []).map((point) => ({
              label: point.label,
              value: point.amountMinor,
              grain: point.grain,
            }))}
            fallbackLoading={insights.isLoading}
            height={200}
          />
          <ChartCard
            title={t('contributions.insights.byFund')}
            loading={insights.isLoading}
            hasData={(insights.data?.byFund.length ?? 0) > 0}
            empty={{ title: t('contributions.insights.byFundEmpty') }}
            height={200}
          >
            <SimpleBarChart
              orientation="horizontal"
              data={(insights.data?.byFund ?? []).map((row) => ({
                label: row.fundName,
                value: row.amountMinor,
                currency: row.currency,
              }))}
              formatValue={(value, item) =>
                formatMoney(value, item?.currency ?? church?.baseCurrency ?? 'GHS', {
                  showSymbol: true,
                })
              }
              height={180}
            />
          </ChartCard>
        </div>
      </CollapsibleInsights>

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={() => {
          setSearch('');
          setFilterMemberId(null);
          setFilterMemberSelected(null);
          setFilterMemberQuery('');
          setFilterFundId(FUND_ANY);
          setFilterMethod(METHOD_ANY);
          setVoidedFilter(VOIDED_HIDE);
          setFilterDateRange({
            preset: 'allTime',
            ...resolvePreset('allTime', timezone),
          });
          setPage(1);
        }}
        labels={{
          filters: t('contributions.filters.title'),
          clearAll: t('contributions.filters.clearAll'),
          applied: (count) => t('contributions.filters.applied', { count }),
        }}
        leading={
          <div className="flex flex-wrap gap-2">
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t('contributions.filters.search')}
              clearLabel={tCommon('search.clear')}
              className="max-w-none sm:w-56"
              aria-label={t('contributions.filters.search')}
            />
            <div className="w-56 min-w-[12rem]">
              <EntityCombobox
                value={filterMemberId}
                onChange={(next) => {
                  setFilterMemberId(next);
                  if (!next) {
                    setFilterMemberSelected(null);
                  } else {
                    const option =
                      filterMemberOptions.find((item) => item.id === next) ?? null;
                    setFilterMemberSelected(option);
                  }
                  setPage(1);
                }}
                options={filterMemberOptions}
                selected={selectedFilterMember}
                onQueryChange={setFilterMemberQuery}
                loading={filterMembers.isPending && !filterMembers.data}
                placeholder={t('contributions.filters.memberPlaceholder')}
                clearLabel={tCommon('action.clearSelection')}
              />
            </div>
          </div>
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-48 min-w-[11rem]">
              <Listbox
                value={filterFundId}
                onChange={(value) => {
                  setFilterFundId(value ?? FUND_ANY);
                  setPage(1);
                }}
                options={[
                  { value: FUND_ANY, label: t('contributions.filters.fundAny') },
                  ...(funds.data?.data ?? []).map((fund) => ({
                    value: fund.id,
                    label: fund.name,
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={filterMethod}
                onChange={(value) => {
                  setFilterMethod(value ?? METHOD_ANY);
                  setPage(1);
                }}
                options={[
                  { value: METHOD_ANY, label: t('contributions.filters.methodAny') },
                  ...CONTRIBUTION_METHODS.map((value) => ({
                    value,
                    label: t(`methods.${value}`),
                  })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="flex flex-col gap-1">
              <DateRangeFilter
                value={filterDateRange}
                onChange={(range) => {
                  setFilterDateRange(range);
                  setPage(1);
                }}
                popoverPortal={popoverPortal}
                labels={{
                  today: tCommon('date.today'),
                  yesterday: tCommon('date.yesterday'),
                  thisWeek: tCommon('date.thisWeek'),
                  lastWeek: tCommon('date.lastWeek'),
                  thisMonth: tCommon('date.thisMonth'),
                  lastMonth: tCommon('date.lastMonth'),
                  thisQuarter: tCommon('date.thisQuarter'),
                  thisYear: tCommon('date.thisYear'),
                  lastYear: tCommon('date.lastYear'),
                  allTime: tCommon('date.allTime'),
                  custom: tCommon('date.custom'),
                  from: t('form.dateFrom'),
                  to: t('form.dateTo'),
                }}
                presets={LIST_FILTER_DATE_RANGE_PRESETS}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={voidedFilter}
                onChange={(value) => {
                  setVoidedFilter(value ?? VOIDED_HIDE);
                  setPage(1);
                }}
                options={[
                  {
                    value: VOIDED_INCLUDE,
                    label: t('contributions.filters.includeVoided'),
                  },
                  {
                    value: VOIDED_HIDE,
                    label: t('contributions.filters.hideVoided'),
                  },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
          </>
        )}
      </FilterBar>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        onRowClick={(row) => setDetailContributionId(row.id)}
        empty={
          <p className="text-body-sm text-fg-secondary">
            {appliedCount > 0 ? t('contributions.emptyFiltered') : t('contributions.empty')}
          </p>
        }
        totals={dataTableMoneyColumnTotals('amount', list.data?.totals, totalsCurrency)}
        totalsLabel={t('contributions.totals')}
        rowActions={(row) => {
          if (row.isVoided) {
            return (
              <Button size="sm" variant="outline" onClick={() => setReasonTarget(row)}>
                {t('contributions.viewReason')}
              </Button>
            );
          }
          if (!canVoidContribution || !row.canVoid) return null;
          return (
            <Button size="sm" variant="outline" onClick={() => setVoidTarget(row)}>
              {t('contributions.void')}
            </Button>
          );
        }}
        actionsHeader={tCommon('table.actions')}
      />

      {list.data?.page && list.data.page.totalPages > 1 ? (
        <Pagination
          page={list.data.page.page}
          pageSize={list.data.page.pageSize}
          totalItems={list.data.page.totalItems}
          totalPages={list.data.page.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) => tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
        />
      ) : null}

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setFundId(null);
            setFundCurrency(null);
            setGiverKind('internal');
            setMemberId(null);
            setExternalPartyId(null);
            setGiverName('');
            setAmountMinor(null);
            setContributionDate(null);
            setProgramId(null);
            setServiceDate(null);
            setMethod('cash');
    setAccountId(null);
            setReference('');
            setNote('');
          }
        }}
      >
        <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
          <form
            key={formKey}
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              if (!fundId || amountMinor == null || !contributionDate || !giverReady) return;
              if (programId && !serviceDate) return;
              record.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t('contributions.record')}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.fund')}</Label>
                    <FundSelect
                      value={fundId}
                      onChange={(next, fund) => {
                        setFundId(next);
                        setFundCurrency(fund?.defaultCurrency ?? null);
                      }}
                      popoverPortal="dialog"
                    />
                  </div>
                  <ContributionGiverFields
                    open={open}
                    kind={giverKind}
                    onKindChange={(next) => {
                      setGiverKind(next);
                      setMemberId(null);
                      setExternalPartyId(null);
                      setGiverName('');
                    }}
                    memberId={memberId}
                    onMemberIdChange={setMemberId}
                    externalPartyId={externalPartyId}
                    onExternalPartyChange={setExternalPartyId}
                    giverName={giverName}
                    onGiverNameChange={setGiverName}
                  />
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.amount')}</Label>
                    <MoneyInput value={amountMinor} onChange={setAmountMinor} currency={currency} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.date')}</Label>
                    <DatePicker
                      value={contributionDate}
                      onChange={setContributionDate}
                      popoverPortal="dialog"
                    />
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <ContributionGatheringFields
                    open={open}
                    programId={programId}
                    serviceDate={serviceDate}
                    onProgramChange={setProgramId}
                    onServiceDateChange={(next) => {
                      setServiceDate(next);
                      if (next) setContributionDate(next);
                    }}
                  />
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.method')}</Label>
                    <Listbox
                      value={method}
                      onChange={(value) => {
                        setMethod((value as typeof method) ?? 'cash');
                        setAccountId(null);
                      }}
                      options={CONTRIBUTION_METHODS.map((value) => ({
                        value,
                        label: t(`methods.${value}`),
                      }))}
                      popoverPortal="dialog"
                    />
                  </div>
                  <ContributionAccountField
                    open={open}
                    method={method}
                    currency={currency}
                    value={accountId}
                    onChange={setAccountId}
                  />
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.reference')}</Label>
                    <Input value={reference} onChange={(event) => setReference(event.target.value)} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label>{t('form.note')}</Label>
                    <Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={2} />
                  </div>
                </div>
              </div>
              <RecordContributionSaveOptions
                addAnother={savePreferences.addAnother}
                onAddAnotherChange={(addAnother) => {
                  const next = { ...savePreferences, addAnother };
                  setSavePreferences(next);
                  persistRecordContributionPreferences(church?.id, next);
                }}
                disabled={record.isPending}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={record.isPending}
                  disabled={
                    !fundId ||
                    amountMinor == null ||
                    !contributionDate ||
                    !giverReady ||
                    Boolean(programId && !serviceDate) ||
                    Boolean(accountKindForMethod(method) && !accountId)
                  }
                >
                  {t('form.save')}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <ContributionDetailDialog
        contributionId={detailContributionId}
        open={Boolean(detailContributionId)}
        onOpenChange={(open) => {
          if (!open) setDetailContributionId(null);
        }}
      />

      <FundDetailDialog
        fundId={detailFundId}
        open={detailFundId !== null}
        onOpenChange={(open) => {
          if (!open) setDetailFundId(null);
        }}
      />

      <Dialog
        open={Boolean(voidTarget)}
        onOpenChange={(next) => {
          if (!next) {
            setVoidTarget(null);
            setVoidReason('');
            setVoidPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('contributions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              voidContribution.mutate();
            }}
          >
            <Tip tipId="giving.contribution.void" />
            <div className="flex flex-col gap-1.5">
              <Label>{t('contributions.voidReason')}</Label>
              <Textarea
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                required
                rows={3}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="void-password">{t('contributions.voidPassword')}</Label>
              <Input
                id="void-password"
                type="password"
                value={voidPassword}
                onChange={(event) => setVoidPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-caption text-fg-secondary">{t('contributions.voidPasswordHint')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setVoidTarget(null)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={voidContribution.isPending}
                disabled={!voidReason.trim() || !voidPassword}
              >
                {t('contributions.voidConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VoidReasonDialog
        open={Boolean(reasonTarget)}
        onClose={() => setReasonTarget(null)}
        reason={reasonTarget?.voidReason}
        voidedByName={reasonTarget?.voidedByName}
      />
    </div>
  );
};
